import { AlertTriangle, Check, X } from 'lucide-react';
import { AnimatedCollapse } from '@reflex/ui/components/shared/AnimatedCollapse';
import type { McpServerSetupDetail } from '@reflex/shared';
import { SetupLogPanel } from './SetupLogPanel';
import { extractServerLogSection } from './parse-events';
import type { LogsState } from './types';

type McpServer = McpServerSetupDetail['servers'][number];

interface McpServerRowProps {
  stepId: string;
  server: McpServer;
  expanded: boolean;
  interactive: boolean;
  logs: LogsState | undefined;
  onToggle: () => void;
}

export function McpServerRow({
  stepId,
  server,
  expanded,
  interactive,
  logs,
  onToggle,
}: McpServerRowProps) {
  const section = logs?.content != null ? extractServerLogSection(logs.content, server.name) : null;
  // A skipped server never ran on the devbox, so there is no log section to
  // expand — its row is inert and carries the skip reason inline instead.
  const isSkipped = server.status === 'skipped';

  const inner = (
    <>
      {server.status === 'ok' ? (
        <Check className="h-3 w-3 shrink-0 text-status-success" />
      ) : isSkipped ? (
        <AlertTriangle className="h-3 w-3 shrink-0 text-status-warning" />
      ) : (
        <X className="h-3 w-3 shrink-0 text-status-error" />
      )}
      <span className="truncate font-mono text-3xs text-foreground/80">{server.name}</span>
      {server.status === 'failed' && <span className="text-3xs text-status-error">failed</span>}
      {isSkipped && <span className="text-3xs text-status-warning">skipped</span>}
    </>
  );

  return (
    <div
      data-testid={`setup-step-${stepId}-server-${server.name}`}
      data-status={server.status}
      className="flex flex-col gap-1"
    >
      {isSkipped ? (
        <div
          data-testid={`setup-step-${stepId}-server-${server.name}-skipped`}
          className="flex items-center gap-2 text-xs"
        >
          {inner}
        </div>
      ) : interactive ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'View'} log for ${server.name}`}
          className="-mx-1 flex items-center gap-2 rounded px-1 py-0.5 text-left text-xs transition-colors hover:bg-muted/50"
        >
          {inner}
        </button>
      ) : (
        <div
          data-testid={`setup-step-${stepId}-server-${server.name}-row`}
          className="flex items-center gap-2 text-xs"
        >
          {inner}
        </div>
      )}
      {isSkipped && server.reason && (
        <div className="ml-5 text-3xs text-muted-foreground">{server.reason}</div>
      )}
      <AnimatedCollapse open={expanded && !isSkipped}>
        <div className="ml-5 rounded-lg border border-border/30 bg-card/50 p-2">
          <SetupLogPanel
            logs={logs}
            section={section}
            emptySectionLabel={`(no log section for ${server.name})`}
            loadingLabel="Loading log…"
          />
        </div>
      </AnimatedCollapse>
    </div>
  );
}
